import type RpcDataSort     from "@/core/services/Rpc/RpcDataSort";
import Core                 from '@/core/Core';
import ProductCategoriesDAO from './ProductCategoriesDAO';
import ProductCategory      from './ProductCategory';
import RpcDataPagination    from "@/core/services/Rpc/RpcDataPagination";

class ProductCategoriesService {

	static productCategoriesMap: Map<any, ProductCategory> | null = null;

  /** Поиск категорий с пагинацией под поисковую выдачу */
  static async search(
    params: Parameters<typeof ProductCategoriesDAO.getList>[0]
  ): Promise<ProductCategory[]> {
    const result = await this.getListPaginated({
      ...params,
      pagination: null, // RpcDataPagination.createForSearch() - нельзя использовать вместе с preloadOnMount и с preloadOnFocus, тк эти прелоады запрещают отправку запросов с паттерном и мы будем видеть статичный массив из 8 записей
    })

    return result.data;
  }

  static async getListPaginated(
    params?: Parameters<typeof ProductCategoriesDAO.getList>[0] & {
      pagination?: RpcDataPagination;
      sort?: RpcDataSort;
    }
  ): Promise<{ data: ProductCategory[]; pagination: RpcDataPagination }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await ProductCategoriesDAO.getList(filters, pagination, sort);

    return {
      data:       Core.utils.hydrateArray(response.data, ProductCategory.fromServer),
      pagination: response.meta.pagination,
    };
  }

	static async getList(
    params: Parameters<typeof ProductCategoriesDAO.getList>[0]
  ): Promise<ProductCategory[]> {
		const response = await ProductCategoriesDAO.getList(params);

		return Core.utils.hydrateArray(response.data, ProductCategory.fromServer);
	}

	static async get(id: number | string, options?: {
		requireAllowedUnits?: boolean;
		requireCharacteristics?: boolean;
	}): Promise<ProductCategory | null> {
		const response = await ProductCategoriesDAO.get(id, options);

		if (response.ok) {
			return ProductCategory.fromServer(response.data);
		}

		return null;
	}

	static async add(category: ProductCategory): Promise<any | null> {
		const response = await ProductCategoriesDAO.add(category);

		if (response.ok) {
			Core.showNotification.success('Успешно создано');
			return ProductCategory.fromServer(response.data);
		}

		return null;
	}

	static async update(category: ProductCategory): Promise<boolean> {
		const response = await ProductCategoriesDAO.update(category);

		if (response.ok) {
			Core.showNotification.success('Успешно обновлено');
		}

		return response.ok;
	}

	static async delete(id: number): Promise<boolean> {
		const response = await ProductCategoriesDAO.delete(id);

		if (response.ok) {
			Core.showNotification.success('Успешно удалено');
		}

		return response.ok;
	}

	static async getPath(productCategoryId: number): Promise<any> {
		const response = await ProductCategoriesDAO.getPath(productCategoryId);

		if (response.ok) {
			return response.data;
		}

		return null;
	}

	/** Рекурсивно поднимается по цепочке категорий и возвращает корневую (самую верхнюю) */
	static getRootCategory(
		categoryId: number,
		categoriesMap: Map<any, ProductCategory> = ProductCategoriesService.getProductCategoriesMap()
	): ProductCategory | null {
		const category = categoriesMap.get(categoryId);

		if (!category) {
			return null;
		}

		// Если есть родитель - идём дальше вверх, иначе это и есть корень
		return category.parentId
			? this.getRootCategory(category.parentId, categoriesMap)
			: category;
	}

	/**
	 * Загружает полное дерево категорий и строит карту.
	 * Пагинация выключена намеренно: карта строит иерархию, и обрезанный список
	 * молча дал бы категории без родителей.
	 */
	static async load(): Promise<void> {
		const response = await ProductCategoriesDAO.getList(
			{ showUnpublished: true },
			RpcDataPagination.createDisabled()
		);

		if (!response.ok) {
			this.productCategoriesMap = null;
			throw new Error('Не удалось загрузить категории товаров');
		}

		const map = new Map();

		Core.utils.hydrateArray(response.data, ProductCategory.fromServer).forEach(category => {
			if (!category.isLeaf) category.children = [];
			map.set(category.id, category);
		});

		map.forEach(category => {
			if (category.parentId && map.get(category.parentId)) {
				map.get(category.parentId).children.push(category.id);
				// используем _parent, а не parent (чтобы не вызвать сеттер)
				category._parent = map.get(category.parentId);
			}
		});

		this.productCategoriesMap = map;
	}

	static getProductCategoriesMap(): Map<any, ProductCategory> {
		if (this.productCategoriesMap === null) {
			throw new Error('Карта категорий не построена: ProductCategoriesService.load() ещё не вызывался');
		}

		return this.productCategoriesMap;
	}

	static resetProductCategoriesMap(): void {
		this.productCategoriesMap = null;
	}

	static async attachCharacteristic(
		categoryId: number,
		characteristicId: number
	): Promise<boolean> {
		const response = await ProductCategoriesDAO.attachCharacteristic(categoryId, characteristicId);

		if (response.ok) {
			Core.showNotification.success('Успешно добавлено');
		}

		return response.ok;
	}

	static async detachCharacteristic(
		categoryId: number,
		characteristicId: number
	): Promise<boolean> {
		const response = await ProductCategoriesDAO.detachCharacteristic(categoryId, characteristicId);

		if (response.ok) {
			Core.showNotification.success('Успешно удалено');
		}

		return response.ok;
	}

	static async updateIsRequiredCharacteristic(
		productCategoryId: number,
		characteristicId: number,
		isRequired: boolean
	): Promise<boolean> {
		const response = await ProductCategoriesDAO.updateCharacteristicRequired(
			productCategoryId,
			characteristicId,
			isRequired
		);

		if (response.ok) {
			Core.showNotification.success('Успешно обновлено');
		}

		return response.ok;
	}

	static async attachAllowedUnit(
		productCategoryId: number,
		unitId: number
	): Promise<boolean> {
		const response = await ProductCategoriesDAO.attachAllowedUnit(productCategoryId, unitId);

		if (response.ok) {
			Core.showNotification.success('Успешно добавлено');
		}

		return response.ok;
	}

	static async detachAllowedUnit(
		productCategoryId: number,
		unitId: number
	): Promise<boolean> {
		const response = await ProductCategoriesDAO.detachAllowedUnit(productCategoryId, unitId);

		if (response.ok) {
			Core.showNotification.success('Успешно удалено');
		}

		return response.ok;
	}

	static showProductCategoryAllowedUnitsDialog(
		productCategory: ProductCategory,
		productCategoryAttachedUnit?: (data?: any) => void,
		productCategoryDetachedUnit?: (data?: any) => void,
	): void {
		Core.dialogs.ProductCategoryAllowedUnitsDialog.show(
			{ productCategory },
			{
				[ProductCategory.Events.AttachUnit]: productCategoryAttachedUnit,
				[ProductCategory.Events.DetachUnit]: productCategoryDetachedUnit,
			}
		);
	}

	static showProductCategoryCharacteristicsDialog(
		productCategory: ProductCategory,
		ProductCategoryAttachCharacteristic?: (data?: any) => void,
		ProductCategoryDetachCharacteristic?: (data?: any) => void,
	): void {
		Core.dialogs.ProductCategoryCharacteristicsDialog.show(
			{ productCategory },
			{
				[ProductCategory.Events.AttachCharacteristic]: ProductCategoryAttachCharacteristic,
				[ProductCategory.Events.DetachCharacteristic]: ProductCategoryDetachCharacteristic,
				close: () => {}
			},
		);
	}
}

export default ProductCategoriesService;